/**
 * Forensic Audio Effects
 * UI feedback sounds for pipeline events
 */

type AudioContextType = AudioContext | null;

export class ForensicAudio {
  private static ctx: AudioContextType = null;

  private static async getCtx(): Promise<AudioContext> {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    return this.ctx;
  }

  /**
   * Play ascending tone for pipeline start
   */
  static async playStart(): Promise<void> {
    try {
      const ctx = await this.getCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.2);
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }

  /**
   * Play celebratory chord for successful completion
   */
  static async playSuccess(): Promise<void> {
    try {
      const ctx = await this.getCtx();
      
      const playTone = (freq: number, delay: number): void => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
        
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + delay + 0.05);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + 0.4);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.4);
      };

      // C major arpeggio
      playTone(523.25, 0);    // C5
      playTone(659.25, 0.1);  // E5
      playTone(783.99, 0.2);  // G5
      playTone(1046.5, 0.3);  // C6
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }

  /**
   * Play descending tone for error
   */
  static async playError(): Promise<void> {
    try {
      const ctx = await this.getCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'square';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(110, ctx.currentTime + 0.3);
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }
}
